import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { setSignedInAccessor, setTokenAccessor, clearTokenCache } from '@/lib/api/client';
import { useAuthStore } from '@/store';

/**
 * Initializes the API client with Clerk's session token accessor and keeps client-side
 * session state in sync with Clerk. Must be rendered inside ClerkProvider and QueryProvider.
 *
 * Sign-out can happen without visiting /logout (e.g. the UserButton menu), so whenever Clerk
 * settles on "signed out" the persisted user and the query cache are cleared here.
 */
export function ApiClientInitializer() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const signedInRef = useRef(false);
  signedInRef.current = !!isSignedIn;

  useEffect(() => {
    setTokenAccessor(async (options) => (await getToken(options)) ?? '');
    setSignedInAccessor(() => signedInRef.current);
  }, [getToken]);

  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    clearAuth();
    clearTokenCache();
    queryClient.clear();
  }, [isLoaded, isSignedIn, clearAuth, queryClient]);

  return null;
}
